import { action, makeObservable, observable } from 'mobx';
import agent from '../agent';
import { minutesToTimeInput } from '~/utils/timeCalc';
import commonStore from './commonStore';

class RosterStore {
    constructor() {
        makeObservable(this, {
            shiftDetailsWhenAppliedFilters: observable,
            filterResources: observable,
            filterValues: observable,
            lastListLoadTime: observable,
            setFilterValues: action,
            transformRosterToEvents: action,
            loadList: action,
            createRosterTemplate: action,
            updateRosterTemplate: action,
            getRosterTemplates: action,
            getRosterTemplateByUserId: action,
            createRosterFromExistingTemplate: action,
            isTemplateAssignedToOthers: action,
            publishRoster: action,
            loadResources: action,
            sendRosterForApproval: action,
            approveOrRejectRoster: action,
            updateRosterAndEmployeeTemplate: action,
            createRosterWithQuickPlanner: action,
            getRosterResources: action,
            getRuleBasedDepartments: action,
            deleteTemplate: action,
            getRuleBasedRoles: action,
            getPublishedRosterTemplates: action,
            downloadExcelReport: action,
            getActiveRosters: action,
            getExpiredRosterDetailsByUserId: action,
            getRosterByUserIdAndDate: action,
            createWorkPatternTemplate: action,
            getWorkPatterns: action,
            assignWorkPattern: action,
            getWorkPatternAssignments: action,
            refreshAssignmentList: action,
            updateWorkPattern: action,
            generateFromPattern: action,
            togglePatternAutomation: action,
            updateAssignmentEndDate: action,
            getWorkPatternSelectionDetails: action,
            updateUserAssignmentPatterns: action,
            validateAssignmentConflicts: action,
            getWorkPatternHistory: action,
            getFCodePlacements: action,
            getClientFCodePlacements: action,
            updateFCodePlacement: action,
        });
    }

    lastListLoadTime = null;
    shiftDetailsWhenAppliedFilters = {};
    filterResources = null
    filterValues = {
        departments: null,
        user_groups: -1,
        status: '',
    };

    setFilterValues(name, value,) {
        const filters = Object.assign({}, this.filterValues);
        filters[name] = value;
        this.filterValues = filters;
    }

    // Transform roster data to calendar event format
    transformRosterToEvents(rosterShifts, translation) {
        if (!rosterShifts || !Array.isArray(rosterShifts)) return [];
        return rosterShifts.map(shift => {
            const isVacation = shift?.shiftsDetails?.source === 'vacation';
            return {
                id: shift.id,
                start: shift.shiftsDetails?.for_date ? `${shift.shiftsDetails.for_date} ${minutesToTimeInput(shift.shiftsDetails.startTime, false)}:00` : `${shift.id} 00:00:00`,
                end: shift.shiftsDetails?.for_date ? `${shift.shiftsDetails.for_date} ${minutesToTimeInput(shift.shiftsDetails.endTime, false)}:00` : `${shift.id} 23:59:59`,
                startTime: shift?.shiftsDetails?.startTime ? shift?.shiftsDetails?.startTime : 0,
                endTime: shift?.shiftsDetails?.endTime ? shift?.shiftsDetails?.endTime : 1440,
                resourceId: shift?.user_id,
                title: isVacation ? translation('Vacation') : (shift?.shiftsDetails?.shiftCode ? shift?.shiftsDetails?.shiftCode : `${translation('No')} ${translation('Title')}`),
                client_id: shift?.client_id || 0,
                status: shift?.shiftsDetails?.status,
                source: isVacation ? 'vacation' : null,
                isPublishedAndEdited: shift?.shiftsDetails?.isPublishedAndEdited,
                rosterId: shift?.shiftsDetails?.employee_roster_id || null,
                shiftType: commonStore.getCompanyShift(shift?.shiftsDetails?.shiftId)?.shift_type || null,
                shiftId: shift?.shiftsDetails?.shiftId || null,
                additionalFCode: shift?.shiftsDetails?.additionalFreeCode ? commonStore.getCompanyShift(parseInt(shift?.shiftsDetails?.additionalFreeCode))?.short_code : null,
                isStandby: !!shift?.shiftsDetails?.isStandBy
            }
        });
    }


    loadList(params, isGuest = false, translation) {
        return agent.Roster.list(params, isGuest)
            .then(
                action((response) => {
                    const events = this.transformRosterToEvents(response?.rosterShifts, translation);
                    this.shiftDetailsWhenAppliedFilters = response.shiftDetailsWhenAppliedFilters;
                    this.filterResources = response.filteredUser;
                    return {
                        events,
                        cycleDetails: response?.cycleDetails,
                        departmentWiseStatusCounts: response?.departmentWiseStatusCounts,
                        workHoursRules: response?.workHoursRules,
                        resourceException: response?.resourceException
                    };
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    createRosterTemplate(data) {
        return agent.Roster.createRosterTemplate(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    updateRosterTemplate(id, data) {
        return agent.Roster.updateRosterTemplate(id, data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    updateRosterAndEmployeeTemplate(id, data) {
        return agent.Roster.updateRosterAndEmployeeTemplate(id, data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getRosterTemplates(values) {
        return agent.Roster.getRosterTemplates(values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getRosterTemplateByUserId(userId, values) {
        return agent.Roster.getRosterTemplateByUserId(userId, values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    createRosterFromExistingTemplate(data) {
        return agent.Roster.createRosterFromExistingTemplate(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    createRosterWithQuickPlanner(data) {
        return agent.Roster.createRosterWithQuickPlanner(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    isTemplateAssignedToOthers(templateId, userId) {
        return agent.Roster.isTemplateAssignedToOthers(templateId, userId)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    publishRoster(data) {
        return agent.Roster.publishRoster(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    loadResources(params) {
        return agent.Roster.loadResources(params)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    sendRosterForApproval(values) {
        return agent.Roster.sendRosterForApproval(values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    approveOrRejectRoster(data) {
        return agent.Roster.approveOrRejectRoster(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getRosterResources(data) {
        return agent.Roster.getRosterResources(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getRuleBasedDepartments(data) {
        return agent.Roster.getRuleBasedDepartments(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    downloadExcelReport(params) {
        return agent.Roster.downloadExcelReport(params)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getPublishedRosterTemplates(values) {
        const { users } = values;
        const _userIds = users.length ? users.join(',') : '';
        return agent.Roster.getPublishedRosterTemplates({ ...values, users: _userIds })
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    deleteTemplate(values) {
        return agent.Roster.deleteTemplate(values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    loadResourceList(values) {
        return agent.Roster.loadResourceList(values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getRuleBasedRoles(params) {
        return agent.Roster.getRuleBasedRoles(params)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getActiveRosters(params = {}) {
        return agent.Roster.getActiveRosters(params)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getExpiredRosterDetailsByUserId(userId, values = {}) {
        return agent.Roster.getExpiredRosterDetailsByUserId(userId, values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getRosterByUserIdAndDate(userId, values = {}) {
        return agent.Roster.getRosterByUserIdAndDate(userId, values)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    createWorkPatternTemplate(data) {
        return agent.Roster.createWorkPatternTemplate(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getWorkPatterns(params = {}) {
        return agent.Roster.getWorkPatterns(params)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    getWorkPatternSelectionDetails(params = {}) {
        return agent.Roster.getWorkPatternSelectionDetails(params)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    assignWorkPattern(data) {
        return agent.Roster.assignWorkPattern(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    getWorkPatternAssignments(params) {
        return agent.Roster.getWorkPatternAssignments(params)
            .then(
                action((list) => {
                    list.time = new Date();
                    this.lastListLoadTime = list.time;
                    return list;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    refreshAssignmentList() {
        this.lastListLoadTime = new Date();
    }

    togglePatternAutomation(assignmentId, data) {
        return agent.Roster.togglePatternAutomation(assignmentId, data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    updateAssignmentEndDate(assignmentId, data) {
        return agent.Roster.updateAssignmentEndDate(assignmentId, data)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    deactivateWorkPattern(id) {
        return agent.Roster.deactivateWorkPattern(id)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    updateWorkPattern(data) {
        return agent.Roster.updateWorkPattern(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    generateFromPattern(data) {
        return agent.Roster.generateFromPattern(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    updateUserAssignmentPatterns(data) {
        return agent.Roster.updateUserAssignmentPatterns(data)
            .then(
                action((res) => {
                    return res;
                })
            )
            .catch(
                action((err) => {
                    throw err;
                })
            );
    }

    validateAssignmentConflicts(params) {
        return agent.Roster.validateAssignmentConflicts(params)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    getWorkPatternHistory(params) {
        return agent.Roster.getWorkPatternHistory(params)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    getFCodePlacements(employeeRosterId) {
        return agent.Roster.getFCodePlacements(employeeRosterId)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    getClientFCodePlacements(params) {
        return agent.Roster.getClientFCodePlacements(params)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

    updateFCodePlacement(id, data) {
        return agent.Roster.updateFCodePlacement(id, data)
            .then(action((res) => res))
            .catch(action((err) => { throw err; }));
    }

}

const _RosterStore = new RosterStore();
export default _RosterStore;